import React from 'react';
import PropTypes from 'prop-types';

import { strings } from './../../../localization';
import ProgressOverlay from './../../../components/common/ProgressOverlay';
import StatusIcon from './../../../components/common/StatusIcon';

import { UPDATE_SETTINGS_TIMEOUT } from './GlobalSettings';

export default class WiFi extends React.PureComponent {


    static propTypes = {
        wifiHotspotsAvailabilityPending: PropTypes.bool.isRequired,
        wifiHotspotsAvailabilityEnabled: PropTypes.bool,
        wifiHotspotsAvailabilityErrorMessage: PropTypes.string,
        applyWiFiHotspotsAvailability: PropTypes.func.isRequired,
        timeoutWiFiHotspotsAvailability: PropTypes.func.isRequired,

        wifiHotspotsCredentialsPending: PropTypes.bool.isRequired,
        wifiHotspotsCredentialsSsid: PropTypes.string,
        wifiHotspotsCredentialsPassword: PropTypes.string,
        wifiHotspotsCredentialsErrorMessage: PropTypes.string,
        applyWiFiHotspotsCredentials: PropTypes.func.isRequired,
        timeoutWiFiHotspotsCredentials: PropTypes.func.isRequired,

        expandOrCollapseStateForUIReferences: PropTypes.func.isRequired,
        wifiHotspotsExpandReference: PropTypes.string.isRequired,
        wifiHotspotsExpandReferenceIsExpanded: PropTypes.bool.isRequired,
        wifiHotspotsCredentialsExpandReference: PropTypes.string.isRequired,
        wifiHotspotsCredentialsExpandReferenceIsExpanded: PropTypes.bool.isRequired
    }

    constructor(props) {

        super(props);

        this.state = {
            wifiHotspotsCredentialsShowPassword: false,
            wifiHotspotsCredentialsSsid: undefined, // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            wifiHotspotsCredentialsPassword: undefined, // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            wifiHotspotsCredentialsInvalidSsidMessage: null,
            wifiHotspotsCredentialsInvalidPasswordMessage: null
        };

        this.applyWiFiHotspotsAvailabilityTimeoutId = undefined;
        this.applyWiFiHotspotsCredentialsTimeoutId = undefined;

    }

    componentWillUnmount() {
        clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId);
        clearTimeout(this.applyWiFiHotspotsCredentialsTimeoutId);
    }

    discardWiFiCredentialsChanges = () => {
        this.setState({
            wifiHotspotsCredentialsSsid: undefined,
            wifiHotspotsCredentialsPassword: undefined,
            wifiHotspotsCredentialsInvalidSsidMessage: null,
            wifiHotspotsCredentialsInvalidPasswordMessage: null
        });

    }

    validateSSID (ssid) {
        if (!ssid) {
            this.setState({
                wifiHotspotsCredentialsInvalidSsidMessage: strings.settings_wifi_ssid_required
            });
            return false;
        }
        if (ssid.length > 32) {
            this.setState({
                wifiHotspotsCredentialsInvalidSsidMessage: strings.settings_wifi_ssid_max_32_characters
            });
            return false;
        }
        this.setState({
            wifiHotspotsCredentialsInvalidSsidMessage: null
        });
        return ssid;
    }

    validatePassword (password) {
        if (!password) {
            this.setState({
                wifiHotspotsCredentialsInvalidPasswordMessage: strings.settings_wifi_password_required
            });
            return false;
        }
        this.setState({
            wifiHotspotsCredentialsInvalidPasswordMessage: null
        });
        return true;
    }

    applyWiFiHotspotsCredentialsState = () => {

        clearTimeout(this.applyWiFiHotspotsCredentialsTimeoutId);

        const wifiHotspotsCredentialsSsid = this.state.wifiHotspotsCredentialsSsid !== undefined ? this.state.wifiHotspotsCredentialsSsid : this.props.wifiHotspotsCredentialsSsid;
        const wifiHotspotsCredentialsPassword = this.state.wifiHotspotsCredentialsPassword !== undefined ? this.state.wifiHotspotsCredentialsPassword : this.props.wifiHotspotsCredentialsPassword;

        // validate this attempt to update creds
        const trimmedSsid = wifiHotspotsCredentialsSsid? wifiHotspotsCredentialsSsid.trim() : null; // RAV-1534 fix assumes the value is trimmed prior to submit
        const validSsid = this.validateSSID(trimmedSsid); // reminder: trimmed in handleSSIDChange()
        if (!validSsid) { return; }
        const validPassword = this.validatePassword(wifiHotspotsCredentialsPassword); // reminder: trimmed in handlePasswordChange()
        if (!validPassword) { return; }

        this.setState({
            wifiHotspotsCredentialsSsid: trimmedSsid, // RAV-1534 trimmed value on submit should be reflected in the UI
            wifiHotspotsCredentialsPassword: wifiHotspotsCredentialsPassword
        })

        this.applyWiFiHotspotsCredentialsTimeoutId = setTimeout(() => {this.props.timeoutWiFiHotspotsCredentials(this.props.wifiHotspotsCredentialsSsid, this.props.wifiHotspotsCredentialsPassword)}, UPDATE_SETTINGS_TIMEOUT);

        this.props.applyWiFiHotspotsCredentials(
            trimmedSsid, // RAV-1534 submit trimmed value
            wifiHotspotsCredentialsPassword,
            this.props.wifiHotspotsCredentialsSsid, // oldSsid (i.e. current SSID)
            this.props.wifiHotspotsCredentialsPassword // oldPassword (i.e. current password)
        )
        .then(
            () => {
                this.discardWiFiCredentialsChanges();
                clearTimeout(this.applyWiFiHotspotsCredentialsTimeoutId);
            },
            () => {clearTimeout(this.applyWiFiHotspotsCredentialsTimeoutId)}
        )
    }

    toggleShowPassword = () => {
        this.setState({wifiHotspotsCredentialsShowPassword: !this.state.wifiHotspotsCredentialsShowPassword});
    }

    handleSSIDChange = (event) => {
        const trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
        this.validateSSID(trimmedValue);
        this.setState({
            wifiHotspotsCredentialsSsid: event.target.value // RAV-1534 (use event's value, not the trimmed one) ASSUMPTION: applyWiFiHotspotsCredentialsState() uses trimmed value!!
        })
    }

    handlePasswordChange = (event) => {
        const trimmedValue = event.target.value? event.target.value.trim() : null; // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
        this.validatePassword(trimmedValue);
        this.setState({
            //RAV-1533 wifiHotspotsCredentialsShowPassword: true,
            wifiHotspotsCredentialsPassword: trimmedValue
        })
    }

    expandOrCollapseSectionsAndSubsections = (isExpanded) => {
        this.props.expandOrCollapseStateForUIReferences(isExpanded,[
            this.props.wifiHotspotsExpandReference,
            this.props.wifiHotspotsCredentialsExpandReference
        ]);
    }

    render() {
        
        var showApplyButton = false;
        // if editing (if state version of ssid and password)
        if (this.state.wifiHotspotsCredentialsSsid !== undefined ||
            this.state.wifiHotspotsCredentialsPassword !== undefined) {
            showApplyButton = true;
        }
        const formActionsClassName =  showApplyButton ? "form-actions" : "form-actions collapsed";

        const planSupportsHotspot = true;
        const settingsGroupsWrapperClassName = planSupportsHotspot? "setting-group-horiz" : "setting-group-horiz display-none";

        let wifiHotspotsCredentialsSsid = this.props.wifiHotspotsCredentialsSsid ? this.props.wifiHotspotsCredentialsSsid : ""; // RAV-1544 sanitizing "undefined"s becasue they will not clear values via state changes (discardWiFiCredentialsChanges() wouldn't work)
        if (this.state.wifiHotspotsCredentialsSsid !== undefined) { // null is a special case (user has cleared the input, input should remain blank if null, render() and apply check for undefined only)
            wifiHotspotsCredentialsSsid = this.state.wifiHotspotsCredentialsSsid ? this.state.wifiHotspotsCredentialsSsid : "";
        }
        let wifiHotspotsCredentialsPassword = this.props.wifiHotspotsCredentialsPassword ? this.props.wifiHotspotsCredentialsPassword : ""; // RAV-1544 sanitizing "undefined"s becasue they will not clear values via state (discardWiFiCredentialsChanges() wouldn't work)
        if (this.state.wifiHotspotsCredentialsPassword !== undefined) {
            wifiHotspotsCredentialsPassword = this.state.wifiHotspotsCredentialsPassword ? this.state.wifiHotspotsCredentialsPassword : "";
        }
        return (
            <div id="component_document_id_wifi">
                <h3>
                    <button
                        className={this.props.wifiHotspotsExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                        onClick={() => {
                            this.expandOrCollapseSectionsAndSubsections(!this.props.wifiHotspotsExpandReferenceIsExpanded)}
                        }>
                        {strings.settings_global_wifi_hotspots_header}
                        <i className="material-icons disclosure">keyboard_arrow_up</i>
                    </button>
                </h3>
                <div>{strings.settings_global_wifi_hotspots_description}</div>
                {planSupportsHotspot?
                    <div className="plan-support-info"><StatusIcon theme="light" type="true" title={strings.status_supported_by_plan} /> {strings.plan_supports_this_feature}</div>
                :
                    <div className="plan-support-info"><StatusIcon theme="light" type="false" title={strings.status_not_supported_by_plan} /> {strings.plan_does_not_support_this_feature}</div>
                }
                <div className={settingsGroupsWrapperClassName}>
                    <div className='setting-group radio-group'>
                        <div>
                            <input type="radio" name="wifi" id="wifi:true"
                                value={true}
                                checked={this.props.wifiHotspotsAvailabilityEnabled === true}
                                onChange={() => {

                                    clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId);
                                    this.applyWiFiHotspotsAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutWiFiHotspotsAvailability(false)}, UPDATE_SETTINGS_TIMEOUT);

                                    this.props.applyWiFiHotspotsAvailability(true)
                                    .then(
                                        () => {clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId)},
                                        () => {clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId)}
                                    )
                                }}
                                disabled={this.props.wifiHotspotsAvailabilityPending}
                            />
                            <label htmlFor='wifi:true' disabled={this.props.wifiHotspotsAvailabilityPending}>{strings.settings_global_wifi_hotspots_enabled_for_your_fleet}</label>
                        </div>
                        <div>
                            <input type="radio" name="wifi" id="wifi:false"
                                value={false}
                                checked={this.props.wifiHotspotsAvailabilityEnabled === false}
                                onChange={() => {

                                    clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId);
                                    this.applyWiFiHotspotsAvailabilityTimeoutId = setTimeout(() => {this.props.timeoutWiFiHotspotsAvailability(true)}, UPDATE_SETTINGS_TIMEOUT);

                                    this.props.applyWiFiHotspotsAvailability(false)
                                    .then(
                                        () => {clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId)},
                                        () => {clearTimeout(this.applyWiFiHotspotsAvailabilityTimeoutId)}
                                    )
                                }}
                                disabled={this.props.wifiHotspotsAvailabilityPending}
                            />
                            <label htmlFor='wifi:false' disabled={this.props.wifiHotspotsAvailabilityPending}>{strings.settings_global_wifi_hotspots_disabled}</label>
                        </div>
                        <div className="error-message">
                            {this.props.wifiHotspotsAvailabilityErrorMessage}
                        </div>
                        {this.props.wifiHotspotsAvailabilityPending ? <ProgressOverlay /> : null }
                    </div>
                    <div className={this.props.wifiHotspotsCredentialsExpandReferenceIsExpanded ? "subgroup" : "subgroup collapsed"}>
                        <h5 className="subgroup-header">
                            <button disabled={true} // SUBSECTION_COLLAPSING_DISABLED
                                className={this.props.wifiHotspotsCredentialsExpandReferenceIsExpanded ? "settings-disclosure-wrapper" : "settings-disclosure-wrapper collapsed"}
                                onClick={() => {
                                    this.props.expandOrCollapseStateForUIReferences(
                                        !this.props.wifiHotspotsCredentialsExpandReferenceIsExpanded,
                                        [this.props.wifiHotspotsCredentialsExpandReference])
                                }}>
                                {strings.settings_global_wifi_hotspots_credentials_header}
                                <i className="material-icons disclosure">keyboard_arrow_up</i>
                            </button>
                        </h5>
                        <div className='setting-group'>
                            <div className="form">
                                <div>
                                <label>{strings.settings_global_wifi_hotspots_credentials_ssid_label}</label>
                                    <input autoComplete="new-password" type="text" value={wifiHotspotsCredentialsSsid} placeholder={strings.settings_global_wifi_hotspots_credentials_ssid_placeholder} onChange={this.handleSSIDChange}></input>
                                </div>
                                <div>
                                    <label>{strings.settings_global_wifi_hotspots_credentials_password_label}</label>
                                    <input autoComplete="new-password" type={this.state.wifiHotspotsCredentialsShowPassword? "text" : "password" } value={wifiHotspotsCredentialsPassword} placeholder={strings.settings_global_wifi_hotspots_credentials_password_placeholder} onChange={this.handlePasswordChange}></input>
                                </div>
                                <div className="show-password">
                                    {this.state.wifiHotspotsCredentialsShowPassword?
                                        <input type="checkbox" value={true} name="showpassword" onClick={this.toggleShowPassword} checked/>
                                    :
                                        <input type="checkbox" value={false} name="showpassword" onClick={this.toggleShowPassword}/>
                                    }
                                    <label htmlFor="showpassword" className="checkbox-label"> {strings.settings_global_wifi_hotspots_credentials_show_password}</label>
                                </div>
                            </div>
                            <div className="error-message form-indent">
                                <span>{this.state.wifiHotspotsCredentialsInvalidSsidMessage}</span>
                                <span className="password">{this.state.wifiHotspotsCredentialsInvalidPasswordMessage}</span>
                            </div>
                            <div className={formActionsClassName}>
                                <button className="btn btn-blue" onClick={this.applyWiFiHotspotsCredentialsState}>{strings.settings_action_apply_new_credentials}</button>
                                <button className="btn btn-outline-red" onClick={this.discardWiFiCredentialsChanges}>{strings.settings_action_discard_changes}</button>
                            </div>
                            <div className="error-message form-indent">
                                {this.props.wifiHotspotsCredentialsErrorMessage}
                            </div>
                            {this.props.wifiHotspotsCredentialsPending ? <ProgressOverlay /> : null }
                        </div>
                        {/*
                        const pending = false;
                        <h5 className="subgroup-header">
                            WiFi Usage Limits
                        </h5>
                        
                        <div className='setting-group switch-group'>
                            <div>
                                <Switch
                                    onChange={() => console.log("Switch changed")}
                                    checked={false}
                                    disabled={pending}
                                    smallFormat={true}
                                />
                                <label disabled={pending}>Turn off WiFi hotspot while vehicle is parked (disabled)</label>
                            </div>
                            <div>
                                <Switch
                                    onChange={() => console.log("Switch changed")}
                                    checked={true}
                                    disabled={pending}
                                    smallFormat={true}
                                />
                                <label disabled={pending}>Turn off for a vehicle if its data use is greater than: [SELECTABLE HERE]</label>
                            </div>
                            {pending ? <ProgressOverlay /> : null }
                        </div>
                        */}
                    </div>
                </div>
            </div>
        );
    }

}